import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { on as onEvent } from '@/services/events'
import { useAuthStore } from './authStore'
import { apiClient } from '@/services/api'

type CreditActivity = {
  id: string
  ts: number
  delta: number
  balance?: number  // 添加余额字段
  source?: string
}

type State = {
  activities: CreditActivity[]
  unread: number
  loading: boolean
  push: (a: CreditActivity) => void
  clearUnread: () => void
  clearAll: () => void
  loadFromAPI: () => Promise<void>
  clearCache: () => void  // 添加清理缓存方法
}

// 创建积分活动存储（不使用persist，手动管理localStorage）
export const useCreditsActivityStore = create<State>()((set, get) => ({
  activities: [],
  unread: 0,
  loading: false,
  push: (a) => {
    const currentUser = useAuthStore.getState().user
    if (!currentUser) return
    
    const newActivities = [a, ...get().activities].slice(0, 100)
    set({ activities: newActivities, unread: get().unread + 1 })
    
    // 保存到用户特定的localStorage
    const storageKey = `credits-activity-${currentUser.id}`
    localStorage.setItem(storageKey, JSON.stringify(newActivities))
  },
  clearUnread: () => set({ unread: 0 }),
  clearAll: () => {
    const currentUser = useAuthStore.getState().user
    if (currentUser) {
      const storageKey = `credits-activity-${currentUser.id}`
      localStorage.removeItem(storageKey)
    }
    set({ activities: [], unread: 0 })
  },
  loadFromAPI: async () => {
    const currentUser = useAuthStore.getState().user
    if (!currentUser) return
    
    try {
      set({ loading: true })
      console.log('🔄 [积分活动] 从API加载积分历史...')
      
      const response = await apiClient.get('/user/points/history?limit=50')
      
      if (response.success && response.data?.pointsLogs) {
        const pointsLogs = response.data.pointsLogs
        
        console.log('🔍 [积分活动] API返回的原始数据:', pointsLogs.slice(0, 3))
        
        // 转换为前端格式
        const activities: CreditActivity[] = pointsLogs.map((log: any) => ({
          id: `${log.userId}-${log.createdAt}`,
          ts: new Date(log.createdAt).getTime(),
          delta: log.amount,
          balance: log.balance, // 确保使用API返回的balance
          source: log.source
        }))
        
        console.log(`✅ [积分活动] 从API加载成功: ${activities.length} 条记录`)
        console.log('🔍 [积分活动] 转换后的活动数据:', activities.slice(0, 3))
        
        // 验证balance字段
        const zeroBalanceCount = activities.filter(a => a.balance === 0).length
        if (zeroBalanceCount > 0) {
          console.warn(`⚠️ [积分活动] 发现 ${zeroBalanceCount} 条记录的余额为0`)
        }
        
        // 更新状态
        set({ activities })
        
        // 保存到localStorage
        const storageKey = `credits-activity-${currentUser.id}`
        localStorage.setItem(storageKey, JSON.stringify(activities))
        
      } else {
        console.warn('⚠️ [积分活动] API响应格式异常:', response)
      }
    } catch (error: any) {
      console.error('❌ [积分活动] 从API加载失败:', error.message)
      
      // 友好的错误处理
      if (error.response?.status === 400) {
        console.warn('⚠️ [积分活动] 请求参数错误，可能是用户未登录或权限不足')
      } else if (error.response?.status === 401) {
        console.warn('⚠️ [积分活动] 用户未登录，跳过API加载')
      } else if (error.response?.status === 403) {
        console.warn('⚠️ [积分活动] 权限不足，跳过API加载')
      } else if (error.response?.status === 404) {
        console.warn('⚠️ [积分活动] API接口不存在')
      } else if (error.response?.status >= 500) {
        console.warn('⚠️ [积分活动] 服务器错误，使用本地缓存')
      }
      
      // 如果API失败，尝试从localStorage加载
      const storageKey = `credits-activity-${currentUser.id}`
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        try {
          const activities = JSON.parse(stored)
          console.log('📦 [积分活动] 从localStorage恢复数据:', activities.length, '条记录')
          set({ activities })
        } catch (parseError) {
          console.error('❌ [积分活动] localStorage数据解析失败:', parseError)
        }
      }
    } finally {
      set({ loading: false })
    }
  },
  clearCache: () => {
    const currentUser = useAuthStore.getState().user
    if (currentUser) {
      const storageKey = `credits-activity-${currentUser.id}`
      localStorage.removeItem(storageKey)
      console.log('🧹 [积分活动] 已清理用户缓存:', storageKey)
    }
    set({ activities: [], unread: 0 })
  }
}))

// 清理旧的积分活动数据
const cleanupOldData = () => {
  const keys = Object.keys(localStorage);
  const oldKeys = keys.filter(key => 
    key === 'credits-activity' || 
    (key.includes('credits-activity') && !key.includes('-'))
  );
  
  if (oldKeys.length > 0) {
    console.log('🧹 清理旧的积分活动数据:', oldKeys);
    oldKeys.forEach(key => localStorage.removeItem(key));
  }
}

// 初始化用户数据
const initializeUserData = async () => {
  // 先清理旧数据
  cleanupOldData();
  
  const currentUser = useAuthStore.getState().user
  console.log('🔄 初始化积分活动数据，当前用户:', currentUser?.id)
  
  if (currentUser) {
    // 🔥 修复：强制从API重新加载数据，忽略localStorage缓存
    console.log('🔄 [积分活动] 强制从API重新加载数据...')
    await useCreditsActivityStore.getState().loadFromAPI()
    
    // 如果API加载失败，才尝试从localStorage加载
    const currentActivities = useCreditsActivityStore.getState().activities
    if (currentActivities.length === 0) {
      const storageKey = `credits-activity-${currentUser.id}`
      const stored = localStorage.getItem(storageKey)
      
      if (stored) {
        try {
          const activities = JSON.parse(stored)
          console.log('📦 [积分活动] API失败，从localStorage恢复:', activities.length, '条记录')
          useCreditsActivityStore.setState({ activities })
        } catch (error) {
          console.error('❌ [积分活动] localStorage数据解析失败:', error)
        }
      }
    }
  } else {
    console.log('🚪 用户未登录，清理积分活动数据')
    useCreditsActivityStore.getState().clearAll()
  }
}

// 监听用户变化
useAuthStore.subscribe((state) => {
  if (state.user) {
    initializeUserData()
  } else {
    useCreditsActivityStore.getState().clearAll()
  }
})

// Subscribe SSE events -> push activity (with user isolation)
onEvent('credits_updated', (p: any) => {
  const currentUser = useAuthStore.getState().user
  console.log('🔔 收到积分更新事件:', { 
    eventUserId: p.userId, 
    currentUserId: currentUser?.id, 
    delta: p.delta, 
    pointsBalance: p.pointsBalance,
    credits: p.credits,
    source: p.source 
  })
  
  // 只处理当前用户的事件
  if (currentUser && p.userId === currentUser.id) {
    console.log('✅ 处理当前用户的积分更新事件')
    useCreditsActivityStore.getState().push({
      id: `${p.userId}-${p.ts || Date.now()}`,
      ts: p.ts || Date.now(),
      delta: typeof p.delta === 'number' ? p.delta : 0,
      balance: p.pointsBalance || p.credits, // 优先使用 pointsBalance，其次使用 credits
      source: p.source,
    })
  } else {
    console.log('🚫 忽略其他用户的积分更新事件')
  }
})


